import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@sara/ui/components/input';

interface ToolbarValue {
  candidateSearch?: string;
}

interface InterviewListToolbarProps {
  value: ToolbarValue;
  onChange: (next: ToolbarValue) => void;
}

export function InterviewListToolbar({ value, onChange }: InterviewListToolbarProps) {
  const [localSearch, setLocalSearch] = useState(value.candidateSearch ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Resets `localSearch` to the external value when it changes. Adjusted during render
  // (React's own pattern for this) rather than in an effect, which would render once
  // with the stale `localSearch` before catching up a frame later.
  const [prevCandidateSearch, setPrevCandidateSearch] = useState(value.candidateSearch);
  if (value.candidateSearch !== prevCandidateSearch) {
    setPrevCandidateSearch(value.candidateSearch);
    setLocalSearch(value.candidateSearch ?? '');
  }

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setLocalSearch(next);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ candidateSearch: next.trim() || undefined });
    }, 300);
  }

  return (
    <div className="relative w-64">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/70"
        strokeWidth={1.6}
      />
      <Input
        placeholder="Search candidates"
        value={localSearch}
        onChange={handleSearchChange}
        className="h-9 rounded-full border-border/70 bg-transparent pl-9 text-[13.5px]"
      />
    </div>
  );
}
